/**
 * The room picks itself back up after a dropped connection (roadmap S-7,
 * proposal P4: "a network drop reconnects within ten seconds without a tap").
 *
 * Before, the room showed Live / Reconnecting / Offline and asked the person
 * to reload. A channel that errored stayed errored. This rejoins on its own:
 * when the browser comes back online, when the tab is shown again, and every
 * few seconds while any of the room's channels is down — the session channel,
 * presence, and the activity channels, which refetch their state on rejoining.
 * Answers already given are on the server; nothing is lost by rejoining.
 */
import { useEffect, useRef } from 'react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { usePresenceStore, type PresenceRole } from '@/stores/prioritization/presenceStore';
import { useToolSessionStore } from '@/stores/prioritization/toolSessionStore';

/** How often to look while the room is down. */
export const RECONNECT_CHECK_MS = 3000;

/** Whether the room's channels are all up. */
export function roomChannelsUp(channelStatus: string | undefined, presenceConnected: boolean): boolean {
  return channelStatus === 'connected' && presenceConnected;
}

export function useRoomReconnect(
  sessionId: string | null | undefined,
  me: { userId: string | null | undefined; userName: string; role: PresenceRole },
): void {
  const meRef = useRef(me);
  meRef.current = me;
  const busy = useRef(false);
  // Only a room that has been up can drop: the first connection is the
  // store's own, and rejoining under it would only interrupt it.
  const wasUp = useRef(false);

  useEffect(() => {
    if (!sessionId) return;

    const rejoin = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const sessions = useSessionStore.getState();
        sessions.unsubscribeFromSessionRealtime(sessionId);
        await sessions.subscribeToSessionRealtime(sessionId);
        const { userId, userName, role } = meRef.current;
        if (userId) await usePresenceStore.getState().join(sessionId, userId, userName, role);
        const tools = useToolSessionStore.getState();
        for (const ts of tools.toolSessions.filter((t) => t.sessionId === sessionId)) {
          tools.unsubscribeFromToolSession(ts.id);
        }
        await tools.subscribeToSessionToolSessions(sessionId);
        void sessions.fetchSessionById(sessionId);
      } finally {
        busy.current = false;
      }
    };

    const isDown = () => {
      const up = roomChannelsUp(
        useSessionStore.getState()._sessionChannelStatus?.[sessionId],
        usePresenceStore.getState().isConnected,
      );
      if (up) wasUp.current = true;
      return wasUp.current && !up;
    };

    const onOnline = () => {
      if (wasUp.current) void rejoin();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && isDown()) void rejoin();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => {
      if (navigator.onLine !== false && isDown()) void rejoin();
    }, RECONNECT_CHECK_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [sessionId]);
}
